<template>
  <ContractReviewDisplayComponent
    :accountData="accountData"
    :contractsToShow="contracts"
    :signerFields="signerFields"
    :companyFields="companyFields"
    :generatorFields="[]"
    :hasGenerator="false"
    exactContractMatch
    @createContainerForStartSigningReady="
      $emit('createContainerForStartSigningReady', $event)
    "
    @disableChangingInformation="$emit('disableChangingInformation')"
  />
</template>

<script setup lang="ts">
import ContractReviewDisplayComponent from "@/signup/ContractReviewDisplayComponent.vue";
import {
  FI_CONTRACTS_WITH_AUTHORIZATION,
  FI_CONTRACTS_WITH_TRANSFER,
} from "@/signup/fi/data/FiContracts";
import { useSignupStore } from "@/signup/signup";
import type { Account } from "@/types";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The Finnish documents: the registry access authorization, or the transfer of the devices into
 * Soldera's registry account, each with the Soldera membership agreement.
 */
const props = defineProps<{
  accountData: Account;
  authorization: boolean;
}>();

defineEmits<{
  createContainerForStartSigningReady: [
    createContainerForStartSigning: () => Promise<void>,
  ];
  disableChangingInformation: [];
}>();

const { t } = useI18n();
const { company } = storeToRefs(useSignupStore());

const signerFields = computed(() => [
  { label: t("account.name"), value: props.accountData.name },
  { label: t("account.registry_code"), value: props.accountData.registry_code },
  { label: t("account.phone_number"), value: props.accountData.phone_number },
]);

const companyFields = computed(() => [
  { label: t("company.name"), value: company.value?.name ?? null },
  {
    label: t("company.registry_code"),
    value: company.value?.registry_code ?? null,
  },
  { label: t("company.vat"), value: company.value?.vat || "-" },
  { label: t("company.address"), value: company.value?.address ?? null },
]);

// One container: everything the chosen path needs is signed together.
const contracts = computed(() => [
  (props.authorization
    ? FI_CONTRACTS_WITH_AUTHORIZATION
    : FI_CONTRACTS_WITH_TRANSFER
  ).map((contract, index) => ({
    id: index + 1,
    key: contract.contract_key,
    contract_title: t(contract.meta.title),
    contract_description: t(contract.meta.description),
    include: contract.include,
    can_include: contract.can_include,
  })),
]);
</script>
